import { useEffect, useState } from "react";
import axios from "axios";

// ========== تعداد رکورد هر آیتم منو ==========
//
// عدد داخل پرانتز کنار هر عنوان منو از همین‌جا می‌آید. هر آیتم با «مسیر» خودش
// شناخته می‌شود (همان link منو) تا هم آیتم‌های گردش سفارش و هم زیرمجموعه‌های
// «داده‌های اولیه» با یک نگاشت پوشش داده شوند.

const BASE_URL = "https://portal-api.mahanpanel.com";

interface CountSource {
  /** مسیر API */
  path: string;
  /** پارامترهای ثابت این آیتم (مثلاً وضعیت سفارش) */
  params?: Record<string, string>;
  /**
   * اندپوینت‌های صفحه‌بندی‌شده فیلد total می‌دهند و فقط یک ردیف گرفته می‌شود؛
   * بقیه آرایه‌ی ساده‌اند و طولشان شمرده می‌شود.
   */
  paginated: boolean;
}

/** نگاشت «مسیر صفحه در منو» → «منبع شمارش». */
const COUNT_SOURCES: Record<string, CountSource> = {
  // ----- گردش سفارش -----
  "/AllOrders": { path: "/proforma-invoices/", paginated: true },
  "/OtherOrders": { path: "/proforma-invoices/other-orders/", paginated: true },
  "/PendingPayments": {
    path: "/proforma-invoices/",
    params: { payment_confirmation: "pending" },
    paginated: true,
  },
  "/ConfirmedPayments": {
    path: "/proforma-invoices/",
    params: { payment_confirmation: "confirmed" },
    paginated: true,
  },
  "/ProformaInvoices": {
    path: "/proforma-invoices/",
    params: { order_status: "proforma" },
    paginated: true,
  },
  "/SalesOrders": {
    path: "/proforma-invoices/",
    params: { order_status: "sales_order" },
    paginated: true,
  },
  "/OrdersInProduction": {
    path: "/proforma-invoices/",
    params: { order_status: "in_production" },
    paginated: true,
  },
  "/ProducedOrders": {
    path: "/proforma-invoices/",
    params: { order_status: "produced" },
    paginated: true,
  },
  "/OrdersInWarehouse": {
    path: "/proforma-invoices/",
    params: { order_status: "in_warehouse" },
    paginated: true,
  },
  "/OrdersExitedWarehouse": {
    path: "/proforma-invoices/",
    params: { order_status: "out_of_warehouse" },
    paginated: true,
  },
  "/LoadedOrders": {
    path: "/proforma-invoices/",
    params: { order_status: "loaded" },
    paginated: true,
  },
  "/DeliveredOrders": {
    path: "/proforma-invoices/",
    params: { order_status: "delivered" },
    paginated: true,
  },

  // ----- گردش کالا (وضعیت قلم، نه سفارش) -----
  "/ReadyToProduceGoods": {
    path: "/proforma-invoices/items/",
    params: { item_status: "sales_order" },
    paginated: true,
  },
  "/GoodsInProduction": {
    path: "/proforma-invoices/items/",
    params: { item_status: "in_production" },
    paginated: true,
  },
  "/ProducedGoods": {
    path: "/proforma-invoices/items/",
    params: { item_status: "produced" },
    paginated: true,
  },
  "/ProductsWarehouse": {
    path: "/proforma-invoices/items/",
    params: { item_status: "in_warehouse" },
    paginated: true,
  },
  "/GoodsExitedWarehouse": {
    path: "/proforma-invoices/items/",
    params: { item_status: "out_of_warehouse" },
    paginated: true,
  },
  "/LoadedGoods": {
    path: "/proforma-invoices/items/",
    params: { item_status: "loaded" },
    paginated: true,
  },
  "/DeliveredGoods": {
    path: "/proforma-invoices/items/",
    params: { item_status: "delivered" },
    paginated: true,
  },

  // ----- داده‌های اولیه -----
  "/ProvincialRegistration": { path: "/provinces/", paginated: false },
  "/CityRegiste": { path: "/cities/", paginated: false },
  "/MeasurementPage": { path: "/measurement-units/", paginated: false },
  "/WarehouseRegistration": { path: "/warehouses/", paginated: false },
  "/GoodsTypeRegistration": { path: "/product-types/", paginated: false },
  "/GoodsAndServicesRegistration": { path: "/goods-services/", paginated: true },
  "/CustomerRegistration": { path: "/customers/", paginated: false },
  "/ProductRegistration": { path: "/products/", paginated: false },
  "/ProductPriceRegistration": { path: "/product-prices/", paginated: false },
  "/CurrencyRegistration": { path: "/currencies/", paginated: false },
  "/PaymentTypeRegistration": { path: "/payment-types/", paginated: false },
  "/ProformaDescriptions": { path: "/proforma-descriptions/", paginated: false },
  "/SalesAvailability": { path: "/sales-availability/", paginated: false },
  "/ContactRegistration": { path: "/contacts/", paginated: false },
  "/SalesSubstitutions": { path: "/sales-substitutions/", paginated: false },

  // ----- مدیریت کاربران -----
  "/manageUser": { path: "/users/", paginated: true },
};

const authHeader = () => {
  const token =
    localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
  return token ? { Authorization: `Bearer ${token}` } : undefined;
};

/**
 * تعداد رکوردهای مسیرهای داده‌شده.
 *
 * فقط مسیرهایی درخواست می‌شوند که کاربر در منو می‌بیند، تا برای نقش‌هایی که به یک
 * بخش دسترسی ندارند درخواست بی‌فایده و خطای ۴۰۳ نفرستیم. هر درخواست مستقل است و
 * شکست یکی روی بقیه اثر ندارد؛ آیتمی که شمارشش نیامد، بدون عدد نمایش داده می‌شود.
 */
const useMenuCounts = (links: string[]): Record<string, number> => {
  const [counts, setCounts] = useState<Record<string, number>>({});
  // کلید پایدار تا effect با هر رندر دوباره اجرا نشود.
  const key = links.slice().sort().join("|");

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const headers = authHeader();
    if (!headers) return;

    const wanted = key.split("|").filter((link) => COUNT_SOURCES[link]);
    if (wanted.length === 0) return;

    const fetchOne = async (link: string): Promise<[string, number] | null> => {
      const source = COUNT_SOURCES[link];
      try {
        const res = await axios.get(`${BASE_URL}${source.path}`, {
          headers,
          params: source.paginated
            ? { page: 1, size: 1, ...(source.params || {}) }
            : source.params,
        });
        if (source.paginated) {
          const total = res.data?.total;
          return typeof total === "number" ? [link, total] : null;
        }
        const items = Array.isArray(res.data) ? res.data : res.data?.items;
        return Array.isArray(items) ? [link, items.length] : null;
      } catch {
        // ۴۰۳/۴۰۴/قطع شبکه: همان آیتم بدون عدد می‌ماند.
        return null;
      }
    };

    const refreshCounts = () => {
      Promise.all(wanted.map(fetchOne)).then((results) => {
        if (cancelled) return;
        setCounts((previous) => {
          const next = { ...previous };
          results.forEach((entry) => {
            if (entry) next[entry[0]] = entry[1];
          });
          return next;
        });
      });
    };
    refreshCounts();
    window.addEventListener("mahan:refresh-menu-counts", refreshCounts);

    return () => {
      cancelled = true;
      window.removeEventListener("mahan:refresh-menu-counts", refreshCounts);
    };
  }, [key]);

  return counts;
};

export default useMenuCounts;
